import { Block, type Data, Fragment, type FragmentMap, Inline, Text } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { dataEquals, dataEqualsObject } from './dataEquals';
import {
    isYSlateBlock,
    isYSlateFragment,
    isYSlateInline,
    isYSlateText,
    type YSlateBlock,
    type YSlateFragment,
    type YSlateInline,
    type YSlateNode,
    type YSlateText,
} from './utils';

/**
 * Returns true if the given yNode representation matches the Slate node.
 */
export function nodeEquals(
    yNode: YSlateBlock | YSlateFragment | YSlateInline | YSlateText,
    node: Block | Inline | Text | Fragment
): boolean {
    if (!yNode.doc) {
        throw new Error('"nodeEquals" expects a Yjs node that is part of a Yjs document');
    }

    if (Text.isText(node)) {
        return textEquals(yNode, node);
    }

    if (Block.isBlock(node)) {
        return blockEquals(yNode, node);
    }

    if (Fragment.isFragment(node)) {
        return fragmentEquals(yNode, node);
    }

    if (Inline.isInline(node)) {
        return inlineEquals(yNode, node);
    }

    assertNever(node);
}

function yNodeDataEquals(yNode: YSlateBlock | YSlateInline, data: Data): boolean {
    // if data is present in one but not in other, baddo
    const yData = yNode.getAttribute('data');

    if (data.size === 0) {
        // If there's no Slate data but there is some yData, fail.
        if (yData && yData.size > 0) {
            return false;
        }
    } else {
        // If there's Slate data, check the data matches
        if (!dataEquals(yData, data)) {
            return false;
        }
    }

    return true;
}

function fragmentsEquals(yNode: YSlateBlock | YSlateInline, fragments: FragmentMap): boolean {
    const yFragments = yNode.getAttribute('fragments');

    if (fragments.size === 0) {
        if (yFragments && yFragments.length > 0) {
            return false;
        }
    } else {
        if (!yFragments || yFragments.length !== fragments.size) {
            return false;
        }

        const fragmentsArr = fragments.toValuesArray();
        for (let i = 0; i < fragmentsArr.length; i++) {
            const fragment = fragmentsArr[i];
            const yFragment = yFragments.get(i);

            if (!yFragment || !nodeEquals(yFragment, fragment)) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Return true if the given Yjs text node is equal to the Slate text node.
 */
function textEquals(yNode: YSlateNode, node: Text): boolean {
    if (!isYSlateText(yNode)) {
        return false;
    }

    if (yNode.getAttribute('key') !== node.key) {
        return false;
    }

    const children: Array<{
        insert: string;
        attributes?: Record<string, object>;
    }> = yNode.toDelta();

    // Slate text always contain at least one leaf
    if (children.length === 0 && node.leaves.size === 1 && node.isEmpty) {
        return true;
    }

    if (node.leaves.size !== children.length) {
        return false;
    }

    for (let leafIndex = 0; leafIndex < node.leaves.size; leafIndex++) {
        const leaf = node.leaves.get(leafIndex);
        assertIsDefined(leaf);

        const yChild = children[leafIndex];
        if (!yChild) {
            return false;
        }

        // Compare the text
        if (leaf.text !== yChild.insert) {
            return false;
        }

        const marks = leaf.marks.toArray();
        const yMarks = new Map(Object.entries(yChild.attributes ?? {}));

        // If we have more marks in Y.XmlText than in the Slate leaf, fail.
        if (marks.length !== yMarks.size) {
            return false;
        }

        // Iterate over the marks in the Slate leaf, and compare them to the attribute
        for (const mark of marks) {
            const yAttribute = yMarks.get(mark.type);
            if (!yAttribute || !dataEqualsObject(yAttribute, mark.data)) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Return true if the Yjs block equals the Slate block.
 */
function blockEquals(yNode: YSlateNode, node: Block): boolean {
    if (!isYSlateBlock(yNode)) {
        return false;
    }

    if (yNode.getAttribute('key') !== node.key) {
        return false;
    }

    if (yNode.getAttribute('type') !== node.type) {
        return false;
    }

    if (Boolean(node.isVoid) !== Boolean(yNode.getAttribute('isVoid'))) {
        return false;
    }

    if (!yNodeDataEquals(yNode, node.data)) {
        return false;
    }

    if (!fragmentsEquals(yNode, node.fragments)) {
        return false;
    }

    return childrenEquals(yNode, node.nodes);
}

/**
 * Return true if the Yjs inline equals the Slate inline.
 */
function inlineEquals(yNode: YSlateNode, node: Inline): boolean {
    if (!isYSlateInline(yNode)) {
        return false;
    }

    if (yNode.getAttribute('key') !== node.key) {
        return false;
    }

    if (yNode.getAttribute('type') !== node.type) {
        return false;
    }

    if (Boolean(yNode.getAttribute('isVoid')) !== Boolean(node.isVoid)) {
        return false;
    }

    if (!yNodeDataEquals(yNode, node.data)) {
        return false;
    }

    if (!fragmentsEquals(yNode, node.fragments)) {
        return false;
    }

    return childrenEquals(yNode, node.nodes);
}

/**
 * Return true if the Yjs fragment equals the Slate fragment.
 */
function fragmentEquals(yNode: YSlateNode, node: Fragment): boolean {
    if (!isYSlateFragment(yNode)) {
        return false;
    }

    if (yNode.getAttribute('key') !== node.key) {
        return false;
    }

    if (yNode.getAttribute('type') !== node.type) {
        return false;
    }

    if (yNode.getAttribute('fragment') !== node.fragment) {
        return false;
    }

    return childrenEquals(yNode, node.nodes);
}

/**
 * Returns true if the given yNode representation matches the childen Slate nodes.
 */
function childrenEquals(
    yNode: YSlateBlock | YSlateFragment | YSlateInline,
    nodes: List<Block | Inline | Text>
): boolean {
    if (yNode.length !== nodes.size) {
        return false;
    }

    for (let i = 0; i < nodes.size; i++) {
        const child = nodes.get(i);
        const yChild = yNode.get(i);

        assertIsDefined(child);

        if (!yChild) {
            return false;
        }

        if (!nodeEquals(yChild, child)) {
            return false;
        }
    }

    return true;
}
